import { Loader2, FileText, Plus, Check, AlertTriangle, CheckCircle2, Info } from "lucide-react";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { formatCurrency } from "../../lib/utils";

export function InvoiceImportDialog({
  open, onOpenChange, importingInvoice, saving, invoiceData,
  selectedItems, setSelectedItems, itemMappings, setItemMappings,
  importProgress, ingredients, onImport, reconcileData,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            Importar desde factura
          </DialogTitle>
          <DialogDescription>
            Revisa los productos extraidos. Asigna cada uno a un ingrediente existente o crealo como nuevo.
          </DialogDescription>
        </DialogHeader>

        {importingInvoice ? (
          <div className="py-12 text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
            <p className="text-muted-foreground">Procesando factura con IA...</p>
            <p className="text-xs text-muted-foreground mt-2">Esto puede tardar unos segundos</p>
          </div>
        ) : saving ? (
          <div className="py-12 text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
            <p className="text-muted-foreground font-medium">{importProgress || "Guardando..."}</p>
            <p className="text-xs text-muted-foreground mt-2">Por favor espera</p>
          </div>
        ) : invoiceData ? (
          <div className="space-y-4">
            {(invoiceData.supplier_name || invoiceData.invoice_date) && (
              <div className="p-3 bg-muted rounded-lg text-sm flex flex-wrap gap-4">
                {invoiceData.supplier_name && <span><strong>Proveedor:</strong> {invoiceData.supplier_name}</span>}
                {invoiceData.invoice_date && <span><strong>Fecha:</strong> {invoiceData.invoice_date}</span>}
                {invoiceData.total != null && <span><strong>Total:</strong> {formatCurrency(invoiceData.total)}</span>}
              </div>
            )}

            <ReconcileSection reconcile={reconcileData} />

            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500 inline-block" /> Coincidencia encontrada</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-400 inline-block" /> Crear nuevo ingrediente</span>
            </div>

            {invoiceData.items?.length > 0 ? (
              <div className="rounded-lg border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="w-10">
                        <Checkbox checked={selectedItems.length === invoiceData.items.length}
                          onCheckedChange={(c) => setSelectedItems(c ? invoiceData.items.map((_, i) => i) : [])} />
                      </TableHead>
                      <TableHead>Producto en factura</TableHead>
                      <TableHead>Cant.</TableHead>
                      <TableHead>Precio ud.</TableHead>
                      <TableHead className="min-w-[200px]">Asignar a ingrediente</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoiceData.items.map((item, index) => {
                      const mapping = itemMappings[index] || { mode: 'new', ingredientId: null };
                      const isExisting = mapping.mode === 'existing';
                      const isSelected = selectedItems.includes(index);
                      return (
                        <TableRow key={index} className={!isSelected ? "opacity-40" : ""}>
                          <TableCell>
                            <Checkbox checked={isSelected}
                              onCheckedChange={(c) => setSelectedItems(c ? [...selectedItems, index] : selectedItems.filter(i => i !== index))} />
                          </TableCell>
                          <TableCell className="font-medium">{item.name || "-"}</TableCell>
                          <TableCell className="font-mono text-sm">{item.quantity ?? "-"} {item.unit || ""}</TableCell>
                          <TableCell className="font-mono text-sm">{item.unit_price ? formatCurrency(item.unit_price) : "-"}</TableCell>
                          <TableCell>
                            <Select value={isExisting ? mapping.ingredientId : "new"}
                              onValueChange={(val) => setItemMappings(prev => ({
                                ...prev,
                                [index]: val === "new" ? { mode: 'new', ingredientId: null } : { mode: 'existing', ingredientId: val }
                              }))}>
                              <SelectTrigger className={`h-8 text-xs ${isExisting ? "border-green-400 bg-green-50 dark:bg-green-900/20" : "border-blue-300 bg-blue-50 dark:bg-blue-900/20"}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="new">
                                  <span className="flex items-center gap-1.5"><Plus className="w-3 h-3 text-blue-500" /> Crear nuevo</span>
                                </SelectItem>
                                {ingredients.length > 0 && (
                                  <>
                                    <div className="px-2 py-1 text-xs text-muted-foreground font-medium border-t mt-1 pt-2">Ingredientes existentes</div>
                                    {ingredients.map(ing => (
                                      <SelectItem key={ing.ingredient_id} value={ing.ingredient_id}>
                                        {ing.name} <span className="text-muted-foreground ml-1 text-xs">(stock: {ing.current_stock} {ing.unit})</span>
                                      </SelectItem>
                                    ))}
                                  </>
                                )}
                              </SelectContent>
                            </Select>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            ) : <p className="text-center text-muted-foreground py-4">No se encontraron productos en la factura</p>}

            {selectedItems.length > 0 && (
              <div className="text-sm text-muted-foreground flex gap-4 pt-1">
                <span><strong className="text-green-600">{selectedItems.filter(i => itemMappings[i]?.mode === 'existing').length}</strong> se actualizaran</span>
                <span><strong className="text-blue-600">{selectedItems.filter(i => itemMappings[i]?.mode !== 'existing').length}</strong> se crearan nuevos (con merma calculada por IA)</span>
              </div>
            )}

            {/* Contabilidad integration notice */}
            {invoiceData.total > 0 && (
              <div className="flex items-start gap-2 text-xs bg-indigo-50 border border-indigo-200 text-indigo-900 rounded-lg px-3 py-2" data-testid="accounting-integration-notice">
                <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-indigo-600" />
                <span>
                  Además de actualizar el stock, archivaremos la factura en Contabilidad con su imagen original y registraremos el gasto de <strong>{formatCurrency(invoiceData.total)}</strong> (categoría <em>Compras</em>). Aparecerá en el P&L, la previsión de caja y el export mensual para el contable.
                </span>
              </div>
            )}
          </div>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancelar</Button>
          <Button onClick={onImport} disabled={saving || importingInvoice || !invoiceData?.items?.length || selectedItems.length === 0}
            data-testid="confirm-import-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
            Importar {selectedItems.length} productos
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReconcileSection({ reconcile }) {
  if (!reconcile) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/40 border border-border/60 rounded-lg px-3 py-2" data-testid="reconcile-loading">
        <Loader2 className="w-3 h-3 animate-spin" />
        Buscando pedidos pendientes para este proveedor…
      </div>
    );
  }

  const { matched_list_id, matched_list_name, matches, missing_from_invoice, summary } = reconcile;

  if (!matched_list_id) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/40 border border-border/60 rounded-lg px-3 py-2" data-testid="reconcile-no-match">
        <Info className="w-3.5 h-3.5" />
        {summary?.suggestion || "No encontré un pedido pendiente que coincida. Se importará como compra directa."}
      </div>
    );
  }

  const discrepantMatches = (matches || []).filter((m) => m.status !== "ok" && m.status !== "extra_in_invoice");
  const extras = (matches || []).filter((m) => m.status === "extra_in_invoice");
  const hasIssues = discrepantMatches.length > 0 || extras.length > 0 || (missing_from_invoice || []).length > 0;
  const deltaPositive = (summary?.total_delta || 0) > 0;

  const statusBadge = (m) => {
    if (m.status === "ok") return <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 dark:bg-emerald-900/30 px-1.5 py-0.5 rounded">OK</span>;
    if (m.status === "qty_mismatch") return <span className="text-[10px] font-semibold text-amber-700 bg-amber-50 dark:bg-amber-900/30 px-1.5 py-0.5 rounded">CANT.</span>;
    if (m.status === "price_mismatch") return <span className="text-[10px] font-semibold text-red-700 bg-red-50 dark:bg-red-900/30 px-1.5 py-0.5 rounded">PRECIO</span>;
    if (m.status === "both_mismatch") return <span className="text-[10px] font-semibold text-red-700 bg-red-50 dark:bg-red-900/30 px-1.5 py-0.5 rounded">CANT+PRECIO</span>;
    if (m.status === "extra_in_invoice") return <span className="text-[10px] font-semibold text-blue-700 bg-blue-50 dark:bg-blue-900/30 px-1.5 py-0.5 rounded">EXTRA</span>;
    return null;
  };

  return (
    <div
      className={`rounded-lg border px-3 py-3 text-sm ${hasIssues ? "border-amber-300 bg-amber-50/70 dark:bg-amber-950/20" : "border-emerald-300 bg-emerald-50/70 dark:bg-emerald-950/20"}`}
      data-testid="reconcile-section"
    >
      <div className="flex items-start gap-2 mb-2">
        {hasIssues ? (
          <AlertTriangle className="w-4 h-4 text-amber-700 mt-0.5 flex-shrink-0" />
        ) : (
          <CheckCircle2 className="w-4 h-4 text-emerald-700 mt-0.5 flex-shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-foreground">
            {hasIssues ? "Discrepancias con tu pedido" : "Coincide con tu pedido"} — <span className="text-muted-foreground font-normal">{matched_list_name}</span>
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Pedido: {formatCurrency(summary.total_list_value)} ·
            Facturado: {formatCurrency(summary.total_invoice_value)}
            {summary.total_delta !== 0 && (
              <span className={deltaPositive ? " text-red-700 font-medium" : " text-emerald-700 font-medium"}>
                {" "}({deltaPositive ? "+" : ""}{formatCurrency(summary.total_delta)})
              </span>
            )}
          </p>
        </div>
      </div>

      {hasIssues && (
        <div className="space-y-1.5 mt-2 max-h-40 overflow-y-auto pr-1">
          {discrepantMatches.map((m, i) => (
            <div key={`d${i}`} className="flex items-center gap-2 text-xs" data-testid="reconcile-discrepancy">
              {statusBadge(m)}
              <span className="font-medium truncate">{m.invoice_item.name}</span>
              <span className="text-muted-foreground text-[11px]">
                {m.qty_delta !== 0 && (
                  <>pediste {m.list_item.quantity}{m.list_item.unit} → facturan {m.invoice_item.quantity}{m.invoice_item.unit} ({m.qty_delta_pct > 0 ? "+" : ""}{m.qty_delta_pct}%)</>
                )}
                {m.qty_delta === 0 && m.price_delta !== 0 && (
                  <>precio: {formatCurrency(m.price_delta)} ({m.price_delta_pct > 0 ? "+" : ""}{m.price_delta_pct}%)</>
                )}
              </span>
            </div>
          ))}
          {extras.map((m, i) => (
            <div key={`e${i}`} className="flex items-center gap-2 text-xs" data-testid="reconcile-extra">
              {statusBadge(m)}
              <span className="font-medium truncate">{m.invoice_item.name}</span>
              <span className="text-muted-foreground text-[11px]">— no estaba en el pedido</span>
            </div>
          ))}
          {(missing_from_invoice || []).map((li, i) => (
            <div key={`m${i}`} className="flex items-center gap-2 text-xs" data-testid="reconcile-missing">
              <span className="text-[10px] font-semibold text-orange-700 bg-orange-50 dark:bg-orange-900/30 px-1.5 py-0.5 rounded">FALTA</span>
              <span className="font-medium truncate">{li.ingredient_name}</span>
              <span className="text-muted-foreground text-[11px]">— pediste {li.quantity}{li.unit} y no viene en la factura</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

